import { useState, type KeyboardEvent } from 'react'

import { useChain } from '../../demo/selectors/chain'
import { useDemoStore } from '../../demo/store'
import { Card, Icon, Toast, ToastHost } from '../../ui'
import { partnershipData } from './data'
import styles from './PartnershipPage.module.css'
import { TierCompareDialog } from './TierCompareDialog'

function activateWithKeyboard(event: KeyboardEvent<HTMLAnchorElement>, action: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  action()
}

function ArrowIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12h15M13 6l6 6-6 6" />
    </svg>
  )
}

export function TierPicker() {
  const [compareOpen, setCompareOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const confirmPartnership = useDemoStore((state) => state.confirmPartnership)
  const step = useChain().steps.find((item) => item.id === '02')
  const done = step?.status === 'done'

  const confirm = () => {
    confirmPartnership(partnershipData.recommendedTier.key)
    setToast(partnershipData.confirmToast)
  }

  return (
    <>
      <div className={styles.tierScope} data-scope="tier-picker">
        <Card>
          <div className={styles.cardHead}>
            <span className={`${styles.statusChip} ${done ? styles.statusDone : styles.statusPending}`}>第 02 步</span>
            <h3>{partnershipData.copy.cardTitle}</h3>
            <span className={styles.cardSubtitle}>{partnershipData.stepSubtitle}</span>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.tiers} data-role="tiers">
              {partnershipData.tiers.map((tier) => (
                <div
                  key={tier.key}
                  className={tier.recommended ? styles.tierRecommended : styles.tierMuted}
                  data-role={`tier-${tier.key}`}
                >
                  {tier.recommended ? (
                    <span className={styles.aiBadge}>
                      <Icon className={styles.sparkleIcon} name="sparkle" size={12} />
                      {partnershipData.copy.aiMatchLabel}
                    </span>
                  ) : null}
                  <div className={tier.recommended ? styles.tierNameRecommended : styles.tierName}>{tier.name}</div>
                  <div className={tier.recommended ? styles.tierUnitRecommended : styles.tierUnit}>{tier.unit}</div>
                </div>
              ))}
            </div>
            <div className={styles.tierDetail} data-role="tier-detail">
              <b>{partnershipData.detailLead}</b>：{partnershipData.detailBody}<br />
              {partnershipData.copy.detailNote}
            </div>
            <div className={styles.benefits} data-role="benefits">
              {partnershipData.benefits.map((benefit, index) => (
                <span key={benefit} className={`${styles.benefitChip} ${index < 3 ? styles.benefitDone : ''}`}>
                  {index < 3 ? <Icon className={styles.checkIcon} name="check" size={12} strokeWidth={2.6} /> : null}
                  {benefit}
                </span>
              ))}
            </div>
            <div className={styles.actionRow}>
              <a
                className={`${styles.action} ${styles.primaryAction}`}
                data-role="confirm"
                role="button"
                tabIndex={0}
                onClick={confirm}
                onKeyDown={(event) => activateWithKeyboard(event, confirm)}
              >
                {partnershipData.copy.confirmLabel}<ArrowIcon />
              </a>
              <a
                className={`${styles.action} ${styles.ghostAction}`}
                data-role="compare"
                role="button"
                tabIndex={0}
                onClick={() => setCompareOpen(true)}
                onKeyDown={(event) => activateWithKeyboard(event, () => setCompareOpen(true))}
              >
                {partnershipData.copy.compareLabel}
              </a>
              <span className={styles.budgetNote}>{partnershipData.copy.budgetNote}</span>
            </div>
          </div>
        </Card>
      </div>
      <TierCompareDialog open={compareOpen} onClose={() => setCompareOpen(false)} />
      {toast ? <ToastHost><Toast onDismiss={() => setToast(null)}>{toast}</Toast></ToastHost> : null}
    </>
  )
}
